"use client";

/**
 * React Error Boundary Component
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Most crashes that reach this boundary in production are not bugs in the current
 * code but a tab still running an older deploy: its page code meets data or chunks
 * from the new build (React error #31 on the business account page was this), or
 * asks for a chunk the deploy deleted. A fresh load fixes every one of those, so the
 * first crash reloads the page on its own. The timestamp stops a genuine bug from
 * looping -- a second crash inside the window shows the fallback instead.
 */
const AUTO_RELOAD_KEY = "error-boundary:auto-reloaded-at";
const AUTO_RELOAD_WINDOW_MS = 60_000;

function tryAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(AUTO_RELOAD_KEY));
    if (last && Date.now() - last < AUTO_RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(AUTO_RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode, disabled site data): without the guard a reload
    // could loop, so show the fallback instead.
    return false;
  }
  window.location.reload();
  return true;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    // Here you could log to an error reporting service
    if (process.env.NODE_ENV === "production") tryAutoReload();
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <div className="flex justify-center">
              <AlertCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
            <p className="text-gray-600">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            <Button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
            >
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
